import { useMemo, useRef, useState } from "react";
import { Braces, Check, ChevronDown, ChevronRight, Code2, CornerDownLeft, Plus, X } from "lucide-react";

interface TransformNodeEditorProps {
  value: Record<string, unknown>;
  onChange: (value: Record<string, unknown>) => void;
  /** id → nombre visible de los nodos del canvas, para etiquetar `nodes.<id>.output`. */
  nodeNames?: Record<string, string>;
  /** Nodo que se está editando: no puede referenciarse a sí mismo. */
  currentNodeId?: string;
  /** El panel está en pantalla completa: hay sitio para el editor grande y la referencia al lado. */
  expanded?: boolean;
}

/** Globals de solo lectura que el sandbox inyecta en el snippet. */
const GLOBALS: { token: string; label: string; hint: string }[] = [
  { token: "trigger.payload", label: "trigger.payload", hint: "Payload del trigger" },
  { token: "state", label: "state", hint: "AppState del usuario" },
  { token: "item", label: "item", hint: "Elemento actual del Split (null fuera de un loop)" },
];

const TEMPLATES: { title: string; description: string; code: string }[] = [
  {
    title: "Objeto",
    description: "Renombrar y limpiar campos del trigger",
    code: `const data = trigger.payload;

return {
  nombre: data.nombre || "Sin nombre",
  total: Number(data.total || 0),
};`,
  },
  {
    title: "Array",
    description: "Mapear una lista para alimentar un Split",
    code: `const items = trigger.payload.items || [];

return items.map((item) => ({
  id: item.id,
  cantidad: Number(item.cantidad || 0),
}));`,
  },
  {
    title: "Condicional",
    description: "Elegir un valor según una condición",
    code: `const total = Number(trigger.payload.total || 0);

return {
  tier: total > 1000 ? "gold" : "standard",
  total,
};`,
  },
];

/** Valida la sintaxis del snippet sin ejecutarlo. */
function validate(code: string): { ok: boolean; error?: string } {
  if (!code.trim()) return { ok: true };
  try {
    // El snippet corre como cuerpo de función, así que `return` en el nivel superior es válido.
    new Function(code);
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}

export default function TransformNodeEditor({
  value,
  onChange,
  nodeNames,
  currentNodeId,
  expanded = false,
}: TransformNodeEditorProps) {
  const code = typeof value.code === "string" ? value.code : "";
  const [referenceOpen, setReferenceOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const validation = useMemo(() => validate(code), [code]);
  const lineCount = useMemo(() => code.split("\n").length, [code]);
  const returnsValue = /(^|[^\w.])return\b/.test(code);

  const nodeTokens = useMemo(
    () =>
      Object.entries(nodeNames ?? {})
        .filter(([id]) => id !== currentNodeId)
        .map(([id, name]) => ({
          token: `nodes[${JSON.stringify(id)}].output`,
          label: name || id.slice(0, 8),
          hint: id,
        })),
    [nodeNames, currentNodeId],
  );

  const setCode = (next: string) => onChange({ ...value, code: next });

  /** Inserta texto en la posición del cursor (o al final si el textarea no está montado). */
  const insert = (token: string) => {
    const el = textareaRef.current;
    if (!el) {
      setCode(code ? `${code}${token}` : token);
      return;
    }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const next = code.slice(0, start) + token + code.slice(end);
    setCode(next);
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = start + token.length;
    });
  };

  const editor = (
    <CodeArea
      ref={textareaRef}
      code={code}
      onChange={setCode}
      lineCount={lineCount}
      rows={expanded ? 24 : 12}
    />
  );

  const reference = (
    <div className="space-y-1.5">
      <p className="text-[10px] text-foreground/40 leading-relaxed">
        Clic para insertar en el cursor. El valor que devuelvas con <code className="font-mono">return</code>{" "}
        es el output del nodo.
      </p>
      <div className="flex flex-wrap gap-1.5">
        {GLOBALS.map((g) => (
          <TokenChip key={g.token} label={g.label} hint={g.hint} onClick={() => insert(g.token)} />
        ))}
      </div>
      {nodeTokens.length > 0 && (
        <>
          <p className="text-[10px] font-semibold text-foreground/40 uppercase tracking-wider pt-1">
            Outputs de otros nodos
          </p>
          <div className="flex flex-wrap gap-1.5">
            {nodeTokens.map((n) => (
              <TokenChip key={n.hint} label={n.label} hint={n.hint} onClick={() => insert(n.token)} />
            ))}
          </div>
        </>
      )}
    </div>
  );

  const status = !code.trim() ? (
    <StatusChip tone="neutral" text="Vacío" />
  ) : !validation.ok ? (
    <StatusChip tone="error" text="Error de sintaxis" icon={<X className="size-2.5" />} />
  ) : !returnsValue ? (
    <StatusChip tone="warn" text="Sin return" />
  ) : (
    <StatusChip tone="ok" text="Válido" icon={<Check className="size-2.5" />} />
  );

  const templates = !code.trim() && (
    <div className="space-y-1.5">
      <p className="text-[10px] font-semibold text-foreground/40 uppercase tracking-wider">
        Empezar desde una plantilla
      </p>
      <div className={expanded ? "grid gap-1.5 sm:grid-cols-3" : "grid gap-1.5"}>
        {TEMPLATES.map((t) => (
          <button
            key={t.title}
            type="button"
            onClick={() => setCode(t.code)}
            className="flex items-start gap-2 rounded-xl border border-black/5 bg-white/50 px-3 py-2 text-left hover:border-accent/30 hover:bg-accent/5 transition-all cursor-pointer"
          >
            <Plus className="size-3 mt-0.5 shrink-0 text-accent" />
            <span className="min-w-0">
              <span className="block text-[11px] font-semibold text-foreground/70">{t.title}</span>
              <span className="block text-[10px] text-foreground/40 truncate">{t.description}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );

  const error = !validation.ok && validation.error && (
    <p className="flex items-start gap-1.5 text-xs text-red-500 font-medium">
      <X className="size-3.5 mt-0.5 shrink-0" />
      {validation.error}
    </p>
  );

  const header = (
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <span className="text-sm font-semibold text-foreground/60 uppercase tracking-widest flex items-center gap-1.5">
        <Code2 className="size-3.5" />
        JavaScript
      </span>
      <div className="flex items-center gap-1.5">
        {status}
        <span className="text-[10px] text-foreground/30">sandbox sin red · 3 s</span>
      </div>
    </div>
  );

  // Expandido hay ancho de sobra: el editor y la referencia van lado a lado.
  if (expanded) {
    return (
      <div className="space-y-3">
        {header}
        <div className="flex gap-4 items-start">
          <div className="min-w-0 flex-1 space-y-3">
            {editor}
            {error}
            {templates}
          </div>
          <div className="w-72 shrink-0 rounded-xl border border-black/5 bg-black/[0.02] p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-foreground/40 mb-2 flex items-center gap-1.5">
              <Braces className="size-3" />
              Variables disponibles
            </p>
            {reference}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {header}

      {/* Referencia de datos: colapsada por defecto para no robarle altura al editor. */}
      <div className="rounded-xl border border-black/5 bg-white/50 overflow-hidden">
        <button
          type="button"
          onClick={() => setReferenceOpen((o) => !o)}
          className="w-full flex items-center gap-1.5 px-3 py-2 text-left hover:bg-black/[0.02] transition-colors cursor-pointer"
        >
          {referenceOpen ? (
            <ChevronDown className="size-3 text-foreground/30 shrink-0" />
          ) : (
            <ChevronRight className="size-3 text-foreground/30 shrink-0" />
          )}
          <Braces className="size-3 text-foreground/40 shrink-0" />
          <span className="text-[11px] font-semibold text-foreground/70">Variables disponibles</span>
          <span className="ml-auto text-[10px] text-foreground/35">
            {GLOBALS.length + nodeTokens.length}
          </span>
        </button>
        {referenceOpen && <div className="px-3 pb-3">{reference}</div>}
      </div>

      {editor}
      {error}
      {templates}
    </div>
  );
}

function StatusChip({
  tone,
  text,
  icon,
}: {
  tone: "ok" | "error" | "warn" | "neutral";
  text: string;
  icon?: React.ReactNode;
}) {
  const styles = {
    ok: "text-green-600 bg-green-500/10 border-green-500/20",
    error: "text-red-600 bg-red-500/10 border-red-500/20",
    warn: "text-amber-600 bg-amber-500/10 border-amber-500/20",
    neutral: "text-foreground/40 bg-black/5 border-black/5",
  }[tone];
  return (
    <span
      className={`flex items-center gap-1 text-[10px] font-bold border px-2 py-0.5 rounded-full shrink-0 ${styles}`}
    >
      {icon}
      {text}
    </span>
  );
}

function TokenChip({
  label,
  hint,
  onClick,
}: {
  label: string;
  hint: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={hint}
      className="max-w-full inline-flex items-center gap-1 rounded-lg border border-black/5 bg-white/70 px-2 py-1 font-mono text-[10px] text-foreground/70 hover:border-accent/30 hover:bg-accent/5 hover:text-accent transition-all cursor-pointer"
    >
      <CornerDownLeft className="size-2.5 shrink-0 opacity-40" />
      <span className="truncate">{label}</span>
    </button>
  );
}

interface CodeAreaProps {
  ref: React.Ref<HTMLTextAreaElement>;
  code: string;
  onChange: (code: string) => void;
  lineCount: number;
  rows: number;
}

/** Textarea monoespaciada con gutter de números de línea y Tab que indenta en vez de saltar de foco. */
function CodeArea({ ref, code, onChange, lineCount, rows }: CodeAreaProps) {
  const gutterRef = useRef<HTMLDivElement | null>(null);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== "Tab") return;
    e.preventDefault();
    const el = e.currentTarget;
    const start = el.selectionStart;
    const next = code.slice(0, start) + "  " + code.slice(el.selectionEnd);
    onChange(next);
    requestAnimationFrame(() => {
      el.selectionStart = el.selectionEnd = start + 2;
    });
  };

  return (
    <div className="rounded-xl border border-black/10 focus-within:border-accent transition-colors overflow-hidden">
      <div className="flex items-center gap-1.5 px-3 py-2 bg-black/3 border-b border-black/5">
        <span className="text-[10px] font-medium text-foreground/30 uppercase tracking-wider">
          JavaScript
        </span>
        <span className="ml-auto text-[10px] text-foreground/25">
          {lineCount} {lineCount === 1 ? "línea" : "líneas"}
        </span>
      </div>
      <div className="flex bg-white/50">
        <div
          ref={gutterRef}
          className="shrink-0 select-none overflow-hidden py-3 pl-3 pr-2 text-right font-mono text-sm leading-relaxed text-foreground/20 bg-black/[0.02]"
          aria-hidden
        >
          {Array.from({ length: lineCount }, (_, i) => (
            <div key={i}>{i + 1}</div>
          ))}
        </div>
        <textarea
          ref={ref}
          value={code}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onScroll={(e) => {
            if (gutterRef.current) gutterRef.current.scrollTop = e.currentTarget.scrollTop;
          }}
          rows={rows}
          // Sin wrap: una línea lógica = una línea visual, para que el gutter no se desalinee.
          wrap="off"
          spellCheck={false}
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          placeholder={"// Ej.\nconst data = trigger.payload;\nreturn { total: Number(data.total || 0) };"}
          className="w-full resize-y bg-transparent px-3 py-3 font-mono text-sm leading-relaxed text-foreground focus:outline-none placeholder:text-foreground/20"
        />
      </div>
    </div>
  );
}
